import { Dialog, DialogBody, DialogHeading, Heading, Text } from '@metservicedev/met-ui';
import React from 'react';

import { createStyles } from '../tss';

const useStyles = createStyles(({ theme }) => ({
  ul: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  li: {
    '&::marker': {
      content: '"•"',
      fontWeight: 'bold',
    },
    paddingLeft: theme.spacing(2),
  },
}));

const Ul: React.FC<React.HTMLProps<'ul'>> = ({ children }) => {
  const { classes } = useStyles();
  return (
    <Text as="ul" className={classes.ul}>{children}</Text>
  );
};

const Li: React.FC<React.HTMLProps<'li'>> = ({ children }) => {
  const { classes } = useStyles();
  return (
    <Text as="li" className={classes.li}>
      {children}
    </Text>
  );
};

type TermsOfUseDialogProps = {
  onClose?: () => void;
  open?: boolean;
};

export const TermsOfUseDialog: React.FC<TermsOfUseDialogProps> = ({ onClose, open }) => (
  <Dialog open={open} onOpenChange={onClose} modal width="3xl">
    <DialogHeading title="Pacific VONA Input System - Terms of Use (August 2025)" />
    <DialogBody style={{ maxHeight: '80vh', overflowY: 'auto' }}>
      <Heading size="md">1 | Definitions</Heading>
      <Text>
        “Platform” means the Pacific VONA Input System, a web application
        (including any APIs, databases and integrations) made available by
        Meteorological Service of New Zealand Limited (“MetService”)
      </Text>
      <Text>
        “Contributor” means an ICAO Contracting State&apos;s designated State
        volcano observatory (SVO) or equivalent scientific agency that submits
        Volcano Observatory Notice to Aviation (“VONA”) through the Platform.
      </Text>
      <Text>
        “Content” means all data, text, attachments and metadata submitted or
        generated by a Contributor when using the Platform.
      </Text>
      <Heading size="md">2 | Purpose and Scope of Service</Heading>
      <Text>
        2.1 The Platform is provided solely as a technical means for
        Contributors to create and disseminate VONA in TAC and IWXXM format via
        Aeronautical Fixed Services (AFS) and, in some cases, via email.
      </Text>
      <Text>
        2.2 MetService&apos;s role is limited to hosting, maintaining and
        transmitting the Platform; MetService does not act as, nor replace, any
        SVO, VAAC, MWO, air navigation service provider or maritime warning
        service.
      </Text>
      <Text>
        2.3 Nothing in these Terms transfers, diminishes or assumes any legal
        duty imposed on a State, SVO or other agency under ICAO Annex 3,
        maritime regulations, or local civil aviation legislation.
      </Text>
      <Heading size="md">3 | Responsibility for Content</Heading>
      <Text>
        3.1 Sole responsibility - Each Contributor is solely responsible for:
      </Text>
      <Ul>
        <Li>
          the accuracy, completeness, timeliness and legality of the Content it
          submits;
        </Li>
        <Li>
          complying with Standards and Recommended Practices in ICAO Annex 3,
          including but not limited to, monitoring volcanoes and deciding
          whether, when and how to issue, update, correct or cancel a VONA.
        </Li>
      </Ul>
      <Text>
        3.2 No editorial control - MetService does not create, verify, validate,
        endorse or continuously monitor any Content and provides no warranty
        that any Content is correct, current or fit for aviation or maritime
        use.
      </Text>
      <Text>
        3.3 No duty to act - MetService is under no obligation to request
        missing Content, correct errors, issue warnings, or take alternative
        measures if a Contributor fails to act or submits incorrect information.
      </Text>
      <Heading size="md">4 | Platform Availability and Service Commitment</Heading>
      <Text>
        4.1 MetService will exercise reasonable skill and care to operate the
        Platform 24 hours a day, excluding:
      </Text>
      <Ul>
        <Li>
          scheduled maintenance (notified at least 24 hours in advance), and
        </Li>
        <Li>
          unforeseen events outside MetService&apos;s reasonable control (e.g.,
          telecom failures, cyberattacks, force majeure).
        </Li>
      </Ul>
      <Text>
        4.2 Outage liability - If an unplanned outage is caused solely by
        MetService&apos;s failure to exercise reasonable skill and care,
        MetService&apos;s liability is limited to the direct costs incurred by
        the affected Contributor in resubmitting the impacted VONA, subject to
        the cap in clause 6.
      </Text>
      <Heading size="md">5 | User Obligations</Heading>
      <Text>
        5.1 Each Contributor must:
      </Text>
      <Ul>
        <Li>
          keep its login credentials secure and restrict access to trained
          personnel;
        </Li>
        <Li>
          ensure Content complies with ICAO specifications and any instructions
          published by MetService;
        </Li>
        <Li>
          maintain independent contingency procedures (e.g., fax, phone,
          email) for issuing VONA if the Platform is unavailable.
        </Li>
      </Ul>
      <Text>
        5.2 Contributors must not upload malicious code, attempt to gain
        unauthorised access, or use the Platform for any purpose other than
        issuing VONA.
      </Text>
      <Heading size="md">6 | Limitation of Liability</Heading>
      <Text>
        6.1 Nothing in these Terms excludes liability that cannot be excluded
        under New Zealand law (e.g., Consumer Guarantees Act 1993 where applicable).
      </Text>
      <Text>
        6.2 Subject to clause 6.1, MetService&apos;s total aggregate liability
        to all Contributors arising in any 12month period is capped at NZD
        10,000.
      </Text>
      <Text>
        6.3 MetService will not be liable (whether in contract, tort, including
        negligence or otherwise) for:
      </Text>
      <Ul>
        <Li>
          any loss caused by errors, omissions, delays or inaccuracies in Content;
        </Li>
        <Li>
          failure by a Contributor to submit or update a VONA;
        </Li>
        <Li>
          indirect, incidental, special or consequential loss, including loss
          of profits, revenue, business, data or goodwill;
        </Li>
        <Li>
          decisions or actions taken by any third party (e.g., airlines,
          airtraffic control, maritime operators) in reliance on the Platform
          or Content.
        </Li>
      </Ul>
      <Heading size="md">7 | Indemnity</Heading>
      <Text>
        Each Contributor indemnifies MetService (and its officers, employees and
        contractors) against any claim, loss or liability arising from:
      </Text>
      <Ul>
        <Li>
          the Contributor&apos;s use of, or inability to use, the Platform;
        </Li>
        <Li>
          any Content the Contributor submits, fails to submit, or that proves
          to be inaccurate or incomplete;
        </Li>
        <Li>
          breach of these Terms by the Contributor or its personnel.
        </Li>
      </Ul>
      <Heading size="md">8 | Compliance and Audit</Heading>
      <Text>
        Contributors acknowledge that MetService may be required to provide
        usage logs or other evidence to the New Zealand Civil Aviation
        Authority, ICAO, or other regulators for audit or incident investigation
        purposes.
      </Text>
      <Heading size="md">9 | Governing Law</Heading>
      <Text>
        These Terms are governed by New Zealand law. Contributors submit to the
        exclusive jurisdiction of the New Zealand courts, without prejudice to
        any mandatory obligations under the laws of their own State.
      </Text>
      <Heading size="md">10 | General</Heading>
      <Text>
        If any provision is held unenforceable, the remainder will continue in
        full force.
      </Text>
      <Text>
        MetService may amend these Terms by giving 30 days&apos; notice; continued
        use of the Platform after the effective date constitutes acceptance of
        the amended Terms.
      </Text>
      <Text>
        Rights and remedies under these Terms are cumulative and do not limit
        any rights at law or in equity.
      </Text>
    </DialogBody>
  </Dialog>
);
